import { AxiosResponse } from "axios";
import { api } from "../http";
import { DefaultRes } from "../types";
import queryStringify from "qs-stringify";
import {
    RecipientCriteria,
    PaginatedResponse,
    SearchGroupItem,
    SearchTeamItem,
    SearchUserItem,
    BrowseGroup,
    CountResponse,
    ResolveResponse,
    RecipientTagsResponse,
    SearchParams,
} from "./compose-search.types";

export class ComposeSearchService {
    private static readonly BASE = '/compose/recipients';

    /**
     * Search groups by name
     */
    public static async searchGroups(params: SearchParams): Promise<PaginatedResponse<SearchGroupItem>> {
        const qs = queryStringify({
            q: params.q,
            limit: params.limit ?? 10,
            offset: params.offset ?? 0,
            activeFilter: params.activeFilter,
        });

        const {
            data: { result },
        } = await api.get<
            DefaultRes<PaginatedResponse<SearchGroupItem>>,
            AxiosResponse<DefaultRes<PaginatedResponse<SearchGroupItem>>>
        >(`${ComposeSearchService.BASE}/search/groups?${qs}`);

        return result;
    }

    /**
     * Search teams by name
     */
    public static async searchTeams(params: SearchParams): Promise<PaginatedResponse<SearchTeamItem>> {
        const qs = queryStringify({
            q: params.q,
            limit: params.limit ?? 10,
            offset: params.offset ?? 0,
            activeFilter: params.activeFilter,
        });

        const {
            data: { result },
        } = await api.get<
            DefaultRes<PaginatedResponse<SearchTeamItem>>,
            AxiosResponse<DefaultRes<PaginatedResponse<SearchTeamItem>>>
        >(`${ComposeSearchService.BASE}/search/teams?${qs}`);

        return result;
    }

    /**
     * Search users by name, email, phone, or employee ID
     */
    public static async searchUsers(params: SearchParams): Promise<PaginatedResponse<SearchUserItem>> {
        const qs = queryStringify({
            q: params.q,
            limit: params.limit ?? 10,
            offset: params.offset ?? 0,
            activeFilter: params.activeFilter,
        });

        const {
            data: { result },
        } = await api.get<
            DefaultRes<PaginatedResponse<SearchUserItem>>,
            AxiosResponse<DefaultRes<PaginatedResponse<SearchUserItem>>>
        >(`${ComposeSearchService.BASE}/search/users?${qs}`);

        return result;
    }

    /**
     * Resolve saved recipient user IDs (the users stored on a message or
     * automated reminder being edited) to the same items searchUsers returns,
     * so their chips can be labelled (ticket 868m9nqx2). Chunked to the API's
     * per-call cap; ids the API omits (deleted, out of the caller's scope) are
     * simply absent from the result.
     */
    public static async lookupUsers(userIds: string[]): Promise<SearchUserItem[]> {
        const ids = Array.from(new Set(userIds.filter(Boolean)));
        const items: SearchUserItem[] = [];
        for (let i = 0; i < ids.length; i += ComposeSearchService.LOOKUP_CHUNK_SIZE) {
            const chunk = ids.slice(i, i + ComposeSearchService.LOOKUP_CHUNK_SIZE);
            const {
                data: { result },
            } = await api.post<
                DefaultRes<{ items: SearchUserItem[] }>,
                AxiosResponse<DefaultRes<{ items: SearchUserItem[] }>>
            >(`${ComposeSearchService.BASE}/users/lookup`, { userIds: chunk });
            items.push(...(result?.items ?? []));
        }
        return items;
    }

    // Kept below the shared WAF's 8KB body-size limit on this endpoint (500 ids ~= 13.5KB).
    private static readonly LOOKUP_CHUNK_SIZE = 250;

    /**
     * Browse organizational hierarchy (groups with nested teams)
     */
    public static async browse(activeFilter?: 'active' | 'inactive' | 'all'): Promise<BrowseGroup[]> {
        const qs = activeFilter ? queryStringify({ activeFilter }) : '';
        const url = qs
            ? `${ComposeSearchService.BASE}/browse?${qs}`
            : `${ComposeSearchService.BASE}/browse`;

        const {
            data: { result },
        } = await api.get<
            DefaultRes<BrowseGroup[]>,
            AxiosResponse<DefaultRes<BrowseGroup[]>>
        >(url);

        return result;
    }

    /**
     * Count recipients matching the given criteria
     */
    public static async count(criteria: RecipientCriteria): Promise<CountResponse> {
        const {
            data: { result },
        } = await api.post<
            DefaultRes<CountResponse>,
            AxiosResponse<DefaultRes<CountResponse>>
        >(`${ComposeSearchService.BASE}/count`, criteria);

        return result;
    }

    /**
     * Resolve criteria to actual user IDs
     */
    public static async resolve(criteria: RecipientCriteria): Promise<ResolveResponse> {
        const {
            data: { result },
        } = await api.post<
            DefaultRes<ResolveResponse>,
            AxiosResponse<DefaultRes<ResolveResponse>>
        >(`${ComposeSearchService.BASE}/resolve`, criteria);

        return result;
    }

    /**
     * Get available tags for filtering, scoped by current criteria
     */
    public static async fetchTags(
        criteria: RecipientCriteria,
        tagEntityType?: 'group' | 'team' | 'user',
        q?: string,
    ): Promise<RecipientTagsResponse> {
        const body: Record<string, any> = { ...criteria };
        if (tagEntityType) body.tagEntityType = tagEntityType;
        if (q) body.query = q;

        const {
            data: { result },
        } = await api.post<
            DefaultRes<RecipientTagsResponse>,
            AxiosResponse<DefaultRes<RecipientTagsResponse>>
        >(`${ComposeSearchService.BASE}/tags`, body);

        return result;
    }
}
